import React, { useState, useMemo, useContext, useEffect } from 'react'
import { CommonInput, CommonModal, CustomSelect } from 'components'
import { UserContext } from 'context';
import { getCompanyDetailsApi, getStatesData } from '_services';
import StorageService from 'utils/StorageServices';
import toast from 'react-hot-toast';

const AddBalanceModal = (props) => {
    const { form, setForm, onSubmit } = props;
    const { states } = useContext(UserContext);
    const [showCoupon, setShowCoupon] = useState(false);
    const [stateList, setStateList] = useState([]);
    const [errors, setErrors] = useState({});

    // Calculate totals with useMemo to ensure consistency
    const { rechargeAmount, couponDiscount, gstAmount, totalAmount } = useMemo(() => {
        const recharge = parseFloat(form.amount) || 0;
        const discount = parseFloat(form.coupon) || 0;
        const gstRate = 0.18;

        const validDiscount = Math.min(discount, recharge);

        // GST is now subtracted (deducted) from the recharge amount
        const gst = form.isCheck ? Math.round((recharge - validDiscount) * gstRate) : 0;
        const discountPrice = validDiscount + gst
        const total = recharge - discountPrice;

        if (form.isCheck) {
            setForm(prevForm => ({
                ...prevForm,
                gstAmount: gst,
                totalAmount: total
            }));
        } else {
            setForm(prevForm => {
                const { gstAmount, ...rest } = prevForm;
                return {
                    ...rest,
                    totalAmount: total
                };
            });
        }

        return {
            rechargeAmount: recharge,
            couponDiscount: validDiscount,
            gstAmount: gst,
            totalAmount: total
        };

    }, [form.amount, form.coupon, form.isCheck]);

    // Validation function
    const validateForm = () => {
        const newErrors = {};

        if (!form.billingName || form.billingName.trim() === '') {
            newErrors.billingName = 'Billing name is required';
        } else if (form.billingName.trim().length < 3) {
            newErrors.billingName = 'Billing name must be at least 3 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(form.billingName.trim())) {
            newErrors.billingName = 'Billing name should contain only letters';
        }

        if (!form.state || !form.state.value) {
            newErrors.state = 'Please select a state';
        }

        if (form.isCheck) {
            if (!form.gst_number || form.gst_number.trim() === '') {
                newErrors.gst_number = 'GST Number is required when generating invoice';
            } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gst_number.trim())) {
                newErrors.gst_number = 'Invalid GST number format';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const clearError = (fieldName) => {
        if (errors[fieldName]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    const handleSubmit = () => {
        if (validateForm()) {
            let payload = { ...form };

            if (!form.isCheck) {
                const { gstAmount, gst_number, ...rest } = payload;
                payload = rest;
            }

            if (onSubmit) {
                onSubmit(payload);
            }
        }
    };

    const propsModalEnhanced = {
        ...props,
        title: 'Payment',
        submitText: `PAY ₹ ${totalAmount}`,
        modalMainClass: "payment-modal",
        onSubmit: handleSubmit,
    }

    const getComapnyDetailsHandle = async () => {
        try {
            const res = await getCompanyDetailsApi({ company_id: StorageService.getCompanyId() });
            const companyDetails = res?.data?.company;

            setForm(prevForm => ({
                ...prevForm,
                billingName: companyDetails?.company || '',
                gst_number: companyDetails?.gst_no || '',
                state: companyDetails?.state || '',
            }));
        } catch (error) {
            console.error("Error fetching company details:", error);
        }
    };

    const getStatesByApi = async () => {
        try {
            let data = { countryCode: "IN", stateCode: "" }
            let response = await getStatesData(data);
            if (response?.responseCode === 200) {
                const optionsState = response?.data?.map((d) => ({
                    ...d,
                    label: d?.name,
                    value: d?.name
                }));

                StorageService.setStates(JSON.stringify([...optionsState]))
                setStateList(optionsState);
            } else {
                if (response) toast.error(response?.responseMessage)
            }
        } catch (error) {
            toast.error("An unexpected error occurred. Please try again.")
        }
    }

    useEffect(() => {
        getStatesByApi()
        getComapnyDetailsHandle()
    }, [])

    return (
        <CommonModal {...propsModalEnhanced}>
            <div className='_lrCl_offCavBody __otr_expCanva'>
                <div className="_othrExp_recBx" style={{ padding: '15px 0px 0px' }}>
                    <div style={{
                        backgroundColor: '#E3FFF1',
                        padding: '16px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <span style={{ fontWeight: '500', color: '#065f46' }}>Wallet Credits</span>
                        <span style={{ fontWeight: '600', color: '#065f46' }}>₹ {rechargeAmount}</span>
                    </div>
                </div>

                <div className="_othrExp_recBx">
                    <div>
                        <h3 style={{ fontSize: '17px', fontWeight: '500', color: '#000000', marginBottom: '16px' }}>
                            Payment Details
                        </h3>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#000000', fontWeight: '400', fontSize: '15px' }}>Recharge amount</span>
                                <span style={{ color: '#000000', fontWeight: '400', fontSize: '15px' }}>₹ {rechargeAmount}</span>
                            </div>

                            {couponDiscount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#000000', fontWeight: '400', fontSize: '15px' }}>Coupon</span>
                                    <span style={{ fontWeight: '400', color: '#dc2626' }}>- ₹ {couponDiscount}</span>
                                </div>
                            )}

                            {/* GST now shown as a deduction (minus) */}
                            {form.isCheck && gstAmount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#000000', fontWeight: '400', fontSize: '15px' }}>GST 18%</span>
                                    <span style={{ fontWeight: '400', color: '#dc2626' }}>- ₹ {gstAmount}</span>
                                </div>
                            )}

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                paddingTop: '12px',
                                borderTop: '1px solid #e5e7eb',
                                fontWeight: '400'
                            }}>
                                <span>Total amount</span>
                                <span>₹ {totalAmount}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="_othrExp_recBx">
                    <div
                        onClick={() => setShowCoupon(!showCoupon)}
                        style={{
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <span style={{ color: '#000000', fontSize: "15px" }}>I have a coupon</span>
                        <span style={{
                            color: '#6b7280',
                            transform: showCoupon ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s'
                        }}>▼</span>
                    </div>

                    {showCoupon && (
                        <div style={{ marginTop: '12px' }}>
                            <CommonInput
                                placeholder="Enter coupon code"
                                value={form.coupon || ''}
                                onChange={(e) => setForm({ ...form, coupon: e.target.value })}
                            />
                        </div>
                    )}
                </div>

                <div className="_othrExp_recBx">
                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ marginBottom: '24px' }}>
                            <CommonInput
                                label="Billing Name"
                                placeholder="Enter billing name"
                                value={form.billingName || ''}
                                onChange={(e) => {
                                    setForm({ ...form, billingName: e.target.value });
                                    clearError('billingName');
                                }}
                                error={errors.billingName}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <CustomSelect
                                label="State"
                                options={stateList}
                                onSelect={(data) => {
                                    setForm({ ...form, state: data });
                                    clearError('state');
                                }}
                                placeholder="Select state"
                                value={stateList?.find((i) => i.value === form?.state?.value)}
                                error={errors.state}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                        <input
                            type="checkbox"
                            id="vehicle1"
                            name="vehicle1"
                            checked={form?.isCheck}
                            onChange={(e) => setForm({ ...form, isCheck: e.target.checked })}
                        />
                        <label htmlFor="vehicle1" style={{ fontSize: '15px', color: '#000000' }}>
                            Generate GST Compliant Invoice (Optional)
                        </label>
                        <br />
                    </div>

                    {form?.isCheck && (
                        <div style={{ marginTop: '12px' }}>
                            <CommonInput
                                label="GST Number"
                                placeholder="Enter GST number"
                                value={form.gst_number || ''}
                                onChange={(e) => {
                                    setForm({ ...form, gst_number: e.target.value });
                                    clearError('gst_number');
                                }}
                                error={errors.gst_number}
                            />
                        </div>
                    )}
                </div>
            </div>
        </CommonModal>
    )
}

export default AddBalanceModal